import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TableLineageModal } from "datasets/components/TableLineageModal";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useDatasetTable from "datasets/hooks/useDatasetTable";
import useTags from "datasets/pages/tables/hooks/useTags";
import { ArrowLeft, Search } from "lucide-react";
import { TablePreviewModal } from "~/components/table-preview/TablePreviewModal";
import { TablePreviewVariant } from "~/components/table-preview/types";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { ChipsMultiSelect, ChipsMultiSelectOption } from "~/components/ui/chips-multi-select";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useToast from "~/components/ui/toast";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import { apiMutate, getApiErrorMessage } from "~/utils/apiClient";
import { TableLineageButton } from "./TableLineageButton";

const { VITE_CATALOG_API } = import.meta.env;

const tagLabel = (tagFQN: string) => tagFQN.replace(".", " / ");

interface DatasetTableProps {
  // Overrides the route params — used when this page is rendered outside the
  // DatasetDetail route tree (the session context sidebar's Tables tab).
  datasetId?: string;
  tableFQN?: string;
  // True when this page is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
  // Renders the "All Tables" link as a back button instead of route
  // navigation — used when embedding this page in the sidebar, which has no
  // `/tables` route to return to.
  onBack?: () => void;
}

export const DatasetTable = ({
  datasetId: datasetIdProp,
  tableFQN: tableFQNProp,
  sessionSidebarView = false,
  onBack,
}: DatasetTableProps = {}) => {
  const { datasetId: routeDatasetId, tableFQN: routeTableFQN } = useParams<{
    datasetId: string;
    tableFQN: string;
  }>();
  const datasetId = datasetIdProp ?? routeDatasetId;
  const tableFQN = tableFQNProp ?? routeTableFQN;
  const { data: table, isLoading, mutate } = useDatasetTable(tableFQN);
  const { data: availableTags } = useTags();
  const { showError } = useToast();
  const canEditGrant = useDatasetPermission("edit:catalog-metadata", datasetId);
  const canEdit = !sessionSidebarView && canEditGrant;

  const [lineageOpen, setLineageOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const [savingTableTags, setSavingTableTags] = useState(false);
  const [savingColumnTags, setSavingColumnTags] = useState<Record<string, boolean>>({});

  const tagOptions: ChipsMultiSelectOption[] = useMemo(
    () =>
      (availableTags ?? []).map((tag) => ({
        value: tag.fullyQualifiedName,
        label: tagLabel(tag.fullyQualifiedName),
      })),
    [availableTags]
  );

  const filteredColumns = useMemo(() => {
    const columns = table?.columns ?? [];
    const search = columnSearch.trim().toLowerCase();
    if (!search) {
      return columns;
    }
    return columns.filter(
      (col) =>
        col.name.toLowerCase().includes(search) || col.description?.toLowerCase().includes(search)
    );
  }, [columnSearch, table?.columns]);

  const handleUpdateTableTags = async (tagFQNs: string[]) => {
    const tags = tagFQNs.map((tagFQN) => ({ tagFQN }));
    setSavingTableTags(true);
    mutate((data) => (data ? { ...data, tags } : data), { revalidate: false });
    try {
      await apiMutate(`${VITE_CATALOG_API}/tables/fqn/${tableFQN}`, "PATCH", { tags });
    } catch (e: any) {
      showError(getApiErrorMessage(e));
    } finally {
      await mutate();
      setSavingTableTags(false);
    }
  };

  const handleUpdateColumnTags = async (columnName: string, tagFQNs: string[]) => {
    const tags = tagFQNs.map((tagFQN) => ({ tagFQN }));
    setSavingColumnTags((prev) => ({ ...prev, [columnName]: true }));
    mutate(
      (data) =>
        data
          ? {
              ...data,
              columns: data.columns.map((c) => (c.name === columnName ? { ...c, tags } : c)),
            }
          : data,
      { revalidate: false }
    );
    try {
      await apiMutate(`${VITE_CATALOG_API}/tables/fqn/${tableFQN}/column/${columnName}`, "PATCH", {
        tags,
      });
    } catch (e: any) {
      showError(getApiErrorMessage(e));
    } finally {
      await mutate();
      setSavingColumnTags((prev) => {
        const next = { ...prev };
        delete next[columnName];
        return next;
      });
    }
  };

  const handleSaveTableDescription = async (next: string) => {
    try {
      await apiMutate(`${VITE_CATALOG_API}/tables/fqn/${tableFQN}`, "PATCH", {
        description: next,
      });
      await mutate();
    } catch (e: any) {
      showError(getApiErrorMessage(e));
      throw e;
    }
  };

  const handleSaveColumnDescription = async (columnName: string, next: string) => {
    try {
      await apiMutate(`${VITE_CATALOG_API}/tables/fqn/${tableFQN}/column/${columnName}`, "PATCH", {
        description: next,
      });
      await mutate();
    } catch (e: any) {
      showError(getApiErrorMessage(e));
      throw e;
    }
  };

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading table"
        className="mx-auto flex w-full max-w-4xl flex-col gap-4"
      >
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    );
  }

  if (!table) {
    return null;
  }

  const location = [table.databaseName, table.databaseSchema].filter(Boolean).join(".");

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-3" />
              All Tables
            </button>
          ) : (
            <Link
              to={`/nova/datasets/${datasetId}/tables`}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-3" />
              All Tables
            </Link>
          )}
          <h1 className="text-xl font-semibold">{table.name}</h1>
          {location && <p className="text-xs text-muted-foreground">Location: {location}</p>}
        </div>
        <div className="flex items-center gap-2">
          <TableLineageButton onClick={() => setLineageOpen(true)} />
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
            View Data
          </Button>
        </div>
      </div>

      <EditableDescription
        value={table.description}
        canEdit={canEdit}
        onSave={handleSaveTableDescription}
        inlineIcon
      />

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-medium">Table Tags</h2>
        <div className="max-w-lg">
          {canEdit ? (
            <ChipsMultiSelect
              selectedValues={table.tags?.map((tag) => tag.tagFQN) ?? []}
              options={tagOptions}
              onChange={handleUpdateTableTags}
              loading={savingTableTags}
              emptyText="No tags found."
            />
          ) : table.tags?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {table.tags.map((tag) => (
                <Badge key={tag.tagFQN} variant="secondary">
                  {tagLabel(tag.tagFQN)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm italic text-muted-foreground">No tags</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-medium">Columns</h2>
        <div className="relative max-w-xs">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by column name..."
            value={columnSearch}
            onChange={(event) => setColumnSearch(event.target.value)}
            className="pl-8"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">Name</TableHead>
              <TableHead className="min-w-[100px]">Type</TableHead>
              <TableHead className="min-w-[300px]">Description</TableHead>
              <TableHead className="min-w-[240px]">Tags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredColumns.map((col) => {
              return (
                <TableRow
                  key={col.fullyQualifiedName ?? col.name}
                  className="[tbody_&]:hover:bg-transparent"
                >
                  <TableCell className="min-w-[180px] text-sm">{col.name}</TableCell>
                  <TableCell className="min-w-[100px] font-mono text-sm text-muted-foreground">
                    {col.dataType}
                  </TableCell>
                  <TableCell className="min-w-[300px] whitespace-normal text-xs text-muted-foreground">
                    <EditableDescription
                      value={col.description}
                      canEdit={canEdit}
                      onSave={(next) => handleSaveColumnDescription(col.name, next)}
                    />
                  </TableCell>
                  <TableCell className="min-w-[240px] whitespace-normal">
                    {canEdit ? (
                      <ChipsMultiSelect
                        selectedValues={col.tags?.map((tag) => tag.tagFQN) ?? []}
                        options={tagOptions}
                        onChange={(tagFQNs) => handleUpdateColumnTags(col.name, tagFQNs)}
                        loading={savingColumnTags[col.name] ?? false}
                        emptyText="No tags found."
                      />
                    ) : col.tags?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {col.tags.map((tag) => (
                          <Badge key={tag.tagFQN} variant="secondary">
                            {tagLabel(tag.tagFQN)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs italic text-muted-foreground">No tags</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <TableLineageModal
        open={lineageOpen}
        onOpenChange={setLineageOpen}
        tableFQN={tableFQN}
        tableName={table.name}
      />
      <TablePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        tableFqn={tableFQN ?? ""}
        tableName={table.name}
        variant={TablePreviewVariant.FULL}
      />
    </div>
  );
};
